/**
 * useUnifiedSearch Hook
 * Combines search across products, fixtures, controls, and categories
 * with relevance scoring and intelligent ranking
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { categoryTemplateService } from '@/services/categoryTemplateService';
import { useMergedCategories } from '@/features/product-library/hooks/useMergedCategories';
import { useProductSearch } from '@/features/product-library/hooks/useProductLibrary';
import {
  mapMergedCategoryToSearchable,
  mapProductToSearchable,
  mapCategoryTemplateToSearchable,
} from '../mappers';
import type { SearchableItem, UnifiedSearchResult } from '../types';

const DEBOUNCE_MS = 150;
const MIN_SEARCH_LENGTH = 2;

/**
 * Calculate match score for a searchable item against query terms
 */
function calculateScore(
  item: SearchableItem,
  queryTerms: string[],
  recentIds: Set<string>,
): number {
  let score = 0;

  const searchableFields = [
    item.displayName,
    item.brand,
    item.model,
    item.category,
    item.subcategory,
    item.secondaryText,
  ]
    .filter(Boolean)
    .map(s => s!.toLowerCase());

  for (const term of queryTerms) {
    // Check each searchable field
    for (const field of searchableFields) {
      // Exact match (whole field)
      if (field === term) {
        score += 100;
        continue;
      }

      // Word starts with term
      const words = field.split(/[\s\-_]+/);
      if (words.some(w => w.startsWith(term))) {
        score += 80;
        continue;
      }

      // Contains term
      if (field.includes(term)) {
        score += 60;
      }
    }

    // Bonus for subcategory match (more specific)
    if (item.subcategory?.toLowerCase().includes(term)) {
      score += 10;
    }
  }

  // Category kind boost (1.2x) - categories appear before products
  if (item.kind === 'category') {
    score *= 1.2;
  }

  // Recent item bonus (+20)
  if (recentIds.has(item.id)) {
    score += 20;
    item.isRecent = true;
  }

  // Has image bonus (+5)
  if (item.icon.type === 'url' && item.icon.url) {
    score += 5;
  }

  return Math.round(score);
}

/**
 * Deduplicate results - categories by path, products by ID
 */
function deduplicateResults(items: SearchableItem[]): SearchableItem[] {
  const seen = new Set<string>();
  return items.filter(item => {
    if (seen.has(item.id)) {
      return false;
    }
    seen.add(item.id);
    return true;
  });
}

export interface UseUnifiedSearchOptions {
  /** Recent item IDs to boost in ranking */
  recentIds?: string[];
  /** Maximum results per section */
  maxCategories?: number;
  maxProducts?: number;
}

/**
 * Unified search hook that searches across all data sources
 */
export function useUnifiedSearch(
  query: string,
  options: UseUnifiedSearchOptions = {},
): UnifiedSearchResult {
  const { recentIds = [], maxCategories = 10, maxProducts = 10 } = options;
  const recentIdSet = useMemo(() => new Set(recentIds), [recentIds]);

  // Debounce the search query
  const debouncedQuery = useDebouncedValue(query, DEBOUNCE_MS);
  const normalizedQuery = debouncedQuery.toLowerCase().trim();
  const queryTerms = normalizedQuery.split(/\s+/).filter(t => t.length > 0);
  const shouldSearch = normalizedQuery.length >= MIN_SEARCH_LENGTH;

  // Fetch fixture templates (cached)
  const { data: fixtureTemplates = [] } = useQuery({
    queryKey: ['category-templates', 'lighting_fixtures'],
    queryFn: () => categoryTemplateService.getByDomain('lighting_fixtures'),
    staleTime: 5 * 60 * 1000,
  });

  // Fetch control templates (cached)
  const { data: controlTemplates = [] } = useQuery({
    queryKey: ['category-templates', 'lighting_controls'],
    queryFn: () => categoryTemplateService.getByDomain('lighting_controls'),
    staleTime: 5 * 60 * 1000,
  });

  // Fetch LV product categories (merged with org categories)
  const { data: lvCategories = [] } = useMergedCategories('lv_products');

  // Fetch products from library (server-side search)
  const {
    data: productSearchResult,
    isLoading: productLoading,
    error: productError,
  } = useProductSearch({
    query: shouldSearch ? debouncedQuery : '',
    limit: maxProducts,
  });

  // Build unified search results
  const result = useMemo((): UnifiedSearchResult => {
    if (!shouldSearch) {
      return {
        query: debouncedQuery,
        categories: [],
        products: [],
        all: [],
        isLoading: false,
      };
    }

    // Convert all sources to SearchableItems
    const allCategories: SearchableItem[] = [];

    // Add fixture templates
    fixtureTemplates.forEach(t => {
      allCategories.push(mapCategoryTemplateToSearchable(t, 'fixture'));
    });

    // Add control templates
    controlTemplates.forEach(t => {
      allCategories.push(mapCategoryTemplateToSearchable(t, 'control'));
    });

    // Add LV product categories
    lvCategories.forEach(c => {
      allCategories.push(mapMergedCategoryToSearchable(c));
    });

    // Score and filter categories
    const scoredCategories = allCategories
      .map(item => {
        const score = calculateScore(item, queryTerms, recentIdSet);
        return { ...item, matchScore: score };
      })
      .filter(item => item.matchScore! > 0)
      .sort((a, b) => b.matchScore! - a.matchScore!)
      .slice(0, maxCategories);

    // Score and filter products
    const products = (productSearchResult?.items || [])
      .map(mapProductToSearchable)
      .map(item => {
        const score = calculateScore(item, queryTerms, recentIdSet);
        return { ...item, matchScore: score };
      })
      .sort((a, b) => b.matchScore! - a.matchScore!)
      .slice(0, maxProducts);

    // Combine all results, sorted by score
    const all = deduplicateResults([...scoredCategories, ...products]).sort(
      (a, b) => (b.matchScore || 0) - (a.matchScore || 0),
    );

    return {
      query: debouncedQuery,
      categories: scoredCategories,
      products,
      all,
      isLoading: productLoading,
      error: productError as Error | undefined,
    };
  }, [
    shouldSearch,
    debouncedQuery,
    queryTerms,
    fixtureTemplates,
    controlTemplates,
    lvCategories,
    productSearchResult,
    productLoading,
    productError,
    recentIdSet,
    maxCategories,
    maxProducts,
  ]);

  return result;
}

export default useUnifiedSearch;
